import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const apiUrl = process.env.REACT_APP_API_URL;

export default function CopyText() {

    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);

    const { token } = useParams();

    useEffect(() => {
        const controller = new AbortController();

        async function loadText() {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(
                    `${apiUrl}shared_text/get_qr_text.php?token=${encodeURIComponent(token)}`,
                    { signal: controller.signal }
                );

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.error || "Unable to load text"
                    );
                }

                setText(result.text);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setError(err.message || "Unable to load text");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        if (token) {
            loadText();
        } else {
            setError("Invalid or missing QR code token.");
            setLoading(false);
        }

        return () => controller.abort();
    }, [token]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setError("");
        } catch {
            setError("Unable to copy text. Please check browser permissions.");
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                bgcolor: "#f5f5f5",
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    width: "100%",
                    maxWidth: 500,
                    borderRadius: 3,
                }}
            >
                <Typography variant="h5" fontWeight="bold" mb={1}>
                    Total Sold Items
                </Typography>

                <Typography color="text.secondary" mb={2}>
                    Copy the complete text below.
                </Typography>

                <Box
                    sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: "#f0f0f0",
                        borderRadius: 2,
                        maxHeight: "45dvh",
                        overflowY: "auto",
                        overflowWrap: "anywhere",
                        whiteSpace: "pre-wrap",
                        userSelect: "text",
                    }}
                >
                    {loading ? "Loading..." : text || "No text was provided."}
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopy}
                    disabled={!text}
                >
                    Copy
                </Button>

                <Snackbar
                    open={copied}
                    autoHideDuration={2500}
                    onClose={() => setCopied(false)}
                >
                    <Alert severity="success">
                        Text copied! You can now paste it.
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={error}
                    autoHideDuration={3500}
                    onClose={() => setError(false)}
                >
                    <Alert severity="error">
                        Clipboard access failed. Use HTTPS and try again.
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
}